import React, { useEffect, useState, useMemo } from 'react';

import Tooltip from 'components/Tooltip/Tooltip';
import AudSummary from '../AudSummary';
import Icons from 'components/Icons';
import './AudienceDropdown.scss';
import { AsyncPaginate } from 'react-select-async-paginate';

import colors from 'theme/Colors.module.scss';

import {
  useLazyGetAudiencesQuery,
  useLazyGetAudienceByIdQuery,
} from 'modules/audiences/audienceApi';

const AudienceDropdown = props => {
  const {
    selectedValue,
    onChange,
    openNewAudience,
    audienceDropdownRefreshToken,
    isDisabled,
  } = props;

  const defaultOptions = useMemo(
    () => [
      { label: 'New Audience', value: -1, type: 'new_audience' },
      { label: 'Open to all', value: 0, data: { id: 0, value: 0 } },
    ],
    []
  );
  const [selectedOption, setSelectedOption] = useState();

  const [getAudiences] = useLazyGetAudiencesQuery();
  const [getAudienceById] = useLazyGetAudienceByIdQuery();

  const onOptionChange = option => {
    setSelectedOption(option);
    if (!option) {
      onChange(null); // 🔥 this ensures parent also resets
      return;
    }
    onChange(option?.data);
  };

  const mapAudienceToOption = x => {
    if (x) {
      return {
        value: x?.id,
        label: x?.name,
        data: x,
      };
    } else {
      return defaultOptions[1];
    }
  };

  const setSelected = aud => {
    const option = mapAudienceToOption(aud);
    onOptionChange(option);
  };

  const loadAudience = async audienceId => {
    try {
      const audienceDetail = await getAudienceById(audienceId).unwrap();
      setSelected(audienceDetail);
    } catch (error) {}
  };

  const loadOptions = async (search, loadedOptions, { page }) => {
    const { data } = await getAudiences({
      search,
      page: page || 1,
      size: 20,
      model: 'audiencedetail',
    });

    // 👇 Transform raw API response here
    const mappedOptions =
      data?.data?.entities?.map(item => ({
        label: item.name,
        value: item.id,
        data: item,
      })) || [];

    return {
      options: mappedOptions,
      hasMore: data?.has_next,
      additional: {
        page: data?.current_page + 1,
      },
    };
  };

  useEffect(() => {
    if (selectedValue > 0) {
      loadAudience(selectedValue);
    } else if (selectedValue === 0) {
      setSelected(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]);

  const NewAudienceOption = ({ innerProps }) => {
    const handleMouseDown = e => {
      e.preventDefault(); // prevent menu from closing
      openNewAudience();
    };
    return (
      <div
        onMouseDown={handleMouseDown} // 👈 Trigger custom action before select closes
        className="audience__list-item list-item__new-audience"
        {...innerProps}
      >
        <Icons.Add size={18} />
        <div>New Audience</div>
      </div>
    );
  };

  const ExistingAudienceOption = ({
    innerProps,
    isFocused,
    isSelected,
    data,
    children,
  }) => {
    if (data?.type === 'new_audience') return NewAudienceOption({ innerProps });

    const includes =
      data.data?.rule_sets?.filter(set => set.logical_op_type) ?? [];
    const excludes =
      data.data?.rule_sets?.filter(set => !set.logical_op_type) ?? [];

    return (
      <div className="audience__list-item" key={data.value} {...innerProps}>
        <div
          className={`list-item__wrapper ${isFocused ? 'focused' : ''} ${
            isSelected ? 'selected' : ''
          }`}
        >
          <div className="list-item__text">{children}</div>
          {data.value !== 0 && (
            <Tooltip
              className="list-item__tooltip"
              fullWidth
              tipText={() => <AudSummary data={{ includes, excludes }} />}
              direction="left"
            />
          )}
        </div>
      </div>
    );
  };

  const customStyles = useMemo(
    () => ({
      control: (provided, state) => ({
        ...provided,
        width: '100%',
        borderColor: state.isFocused ? colors.primary900 : provided.borderColor,
        boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary500}` : 'none',
        '&:hover': {
          borderColor: colors.primary900,
        },
      }),
    }),
    []
  );

  return (
    <AsyncPaginate
      key={audienceDropdownRefreshToken}
      className="audienceDropdown"
      inputId="audienceInput"
      isClearable
      cacheOptions
      defaultOptions={defaultOptions}
      loadOptions={loadOptions}
      components={{ Option: ExistingAudienceOption }}
      additional={{
        page: 1,
      }}
      debounceTimeout={400}
      value={selectedOption}
      // menuIsOpen={true} // force menu open for debug purpose
      onChange={onOptionChange}
      styles={customStyles}
      isDisabled={isDisabled}
    />
  );
};

export default AudienceDropdown;
